import { ConstStrategyData } from '../typings/constants';
import { CampaignId, CampaignPlatform } from '../typings/domain';
import { GdCampaignBudgetShowMode } from '../typings/graphql-grid';
import {
    AttributionModel,
    DnaStrategyType,
    OptimizationCriteria,
    Strategy,
    StrategyEditorInitData,
    StrategyEditorValidationResult,
    StrategyForEditor
} from '../typings/strategy';

export enum StrategyEditorActionType {
    OPEN = 'StrategyEditorAction/OPEN',
    CLOSE = 'StrategyEditorAction/CLOSE',
    INIT = 'StrategyEditorAction/INIT',
    CHANGE_TYPE = 'StrategyEditorAction/CHANGE_TYPE',
    CHANGE_PLATFORM = 'StrategyEditorAction/CHANGE_PLATFORM',
    CHANGE_ATTRIBUTION_MODEL = 'StrategyEditorAction/CHANGE_ATTRIBUTION_MODEL',
    CHANGE_AVG_CLICK_PRICE = 'StrategyEditorAction/CHANGE_AVG_CLICK_PRICE',
    BLUR_AVG_CLICK_PRICE = 'StrategyEditorAction/BLUR_AVG_CLICK_PRICE',
    CHANGE_WEEK_BUNDLE = 'StrategyEditorAction/CHANGE_WEEK_BUNDLE',
    BLUR_WEEK_BUNDLE = 'StrategyEditorAction/BLUR_WEEK_BUNDLE',
    CHANGE_AVG_CPA_PRICE = 'StrategyEditorAction/CHANGE_AVG_CPA_PRICE',
    BLUR_AVG_CPA_PRICE = 'StrategyEditorAction/BLUR_AVG_CPA_PRICE',
    CHANGE_CONTEXT_LIMIT = 'StrategyEditorAction/CHANGE_CONTEXT_LIMIT',
    CHANGE_CPC_HOLD = 'StrategyEditorAction/CHANGE_CPC_HOLD',
    CHANGE_DAILY_BUDGET_MODE = 'StrategyEditorAction/CHANGE_DAILY_BUDGET_MODE',
    CHANGE_DAILY_BUDGET = 'StrategyEditorAction/CHANGE_DAILY_BUDGET',
    BLUR_DAILY_BUDGET = 'StrategyEditorAction/BLUR_DAILY_BUDGET',
    CHANGE_GOAL_ID = 'StrategyEditorAction/CHANGE_GOAL_ID',
    CHANGE_MAX_CLICK_PRICE = 'StrategyEditorAction/CHANGE_MAX_CLICK_PRICE',
    BLUR_MAX_CLICK_PRICE = 'StrategyEditorAction/BLUR_MAX_CLICK_PRICE',
    CHANGE_PROFITABILITY = 'StrategyEditorAction/CHANGE_PROFITABILITY',
    BLUR_PROFITABILITY = 'StrategyEditorAction/BLUR_PROFITABILITY',
    CHANGE_RESERVE_RETURN = 'StrategyEditorAction/CHANGE_RESERVE_RETURN',
    CHANGE_ROI_COEF = 'StrategyEditorAction/CHANGE_ROI_COEF',
    BLUR_ROI_COEF = 'StrategyEditorAction/BLUR_ROI_COEF',
    CHANGE_SEPARATE_BIDDING = 'StrategyEditorAction/CHANGE_SEPARATE_BIDDING',
    CHANGE_WEEKLY_BUDGET = 'StrategyEditorAction/CHANGE_WEEKLY_BUDGET',
    BLUR_WEEKLY_BUDGET = 'StrategyEditorAction/BLUR_WEEKLY_BUDGET',
    VALIDATE_AND_ATTEMPT_SAVE = 'StrategyEditorAction/VALIDATE_AND_ATTEMPT_SAVE',
    APPLY_VALIDATION_RESULT = 'StrategyEditorAction/APPLY_VALIDATION_RESULT',
    REVERT_EDITING = 'StrategyEditorAction/REVERT_EDITING',
    CHANGE_OPTIMIZATION_CRITERIA = 'StrategyEditorAction/CHANGE_OPTIMIZATION_CRITERIA',
    TOGGLE_AUXILIARY_PARAMS_VISIBILTY = 'StrategyEditorAction/TOGGLE_AUXILIARY_PARAMS_VISIBILTY',
    HIDE_ROI_MEANINGFUL_GOALS_TOOLTIP = 'StrategyEditorAction/HIDE_ROI_MEANINGFUL_GOALS_TOOLTIP',
    CLEAR_ROI_GOAL_ID_ERROR = 'StrategyEditorAction/CLEAR_ROI_GOAL_ID_ERROR',
    TOGGLE_CPA_PAY_FOR_CONVERSION = 'StrategyEditorAction/TOGGLE_CPA_PAY_FOR_CONVERSION'
}

export interface OpenStrategyEditorAction {
    type: StrategyEditorActionType.OPEN;
    editorId: string;
}

export function openStrategyEditor(editorId: string): OpenStrategyEditorAction {
    return {
        type: StrategyEditorActionType.OPEN,
        editorId
    };
}

export interface CloseStrategyEditorAction {
    type: StrategyEditorActionType.CLOSE;
    editorId: string;
}

export function closeStrategyEditor(editorId: string): CloseStrategyEditorAction {
    return {
        type: StrategyEditorActionType.CLOSE,
        editorId
    };
}

export interface InitStrategyEditorsAction {
    type: StrategyEditorActionType.INIT;
    payload: {
        editorId: string;
        strategy: StrategyForEditor;
        isChooserDisabled: boolean;
    }[];
}

export function initStrategyEditors(payload: StrategyEditorInitData[]): InitStrategyEditorsAction {
    return {
        type: StrategyEditorActionType.INIT,
        payload
    };
}

export interface ChangeStrategyTypeAction {
    type: StrategyEditorActionType.CHANGE_TYPE;
    editorId: string;
    strategyType: DnaStrategyType;
}

export function changeStrategyType(editorId: string, strategyType: DnaStrategyType): ChangeStrategyTypeAction {
    return {
        type: StrategyEditorActionType.CHANGE_TYPE,
        editorId,
        strategyType
    };
}

export interface ChangeStrategyPlatformAction {
    type: StrategyEditorActionType.CHANGE_PLATFORM;
    editorId: string;
    platform: CampaignPlatform;
}

export function changeStrategyPlatform(editorId: string, platform: CampaignPlatform): ChangeStrategyPlatformAction {
    return {
        type: StrategyEditorActionType.CHANGE_PLATFORM,
        editorId,
        platform
    };
}

export interface ChangeStrategyAttributionModelAction {
    type: StrategyEditorActionType.CHANGE_ATTRIBUTION_MODEL;
    editorId: string;
    attributionModel: AttributionModel;
}

export function changeStrategyAttributionModel(
    editorId: string,
    attributionModel: AttributionModel
): ChangeStrategyAttributionModelAction {
    return {
        type: StrategyEditorActionType.CHANGE_ATTRIBUTION_MODEL,
        editorId,
        attributionModel
    };
}

export interface ChangeStrategyAvgClickPriceAction {
    type: StrategyEditorActionType.CHANGE_AVG_CLICK_PRICE;
    editorId: string;
    avgClickPrice: string;
}

export function changeStrategyAvgClickPrice(
    editorId: string,
    avgClickPrice: string
): ChangeStrategyAvgClickPriceAction {
    return {
        type: StrategyEditorActionType.CHANGE_AVG_CLICK_PRICE,
        editorId,
        avgClickPrice
    };
}

export interface BlurStrategyAvgClickPriceAction {
    type: StrategyEditorActionType.BLUR_AVG_CLICK_PRICE;
    editorId: string;
    constants?: ConstStrategyData;
}

export function blurStrategyAvgClickPrice(
    editorId: string,
    constants?: ConstStrategyData
): BlurStrategyAvgClickPriceAction {
    return {
        type: StrategyEditorActionType.BLUR_AVG_CLICK_PRICE,
        editorId,
        constants
    };
}

export interface ChangeStrategyAvgCpaPriceAction {
    type: StrategyEditorActionType.CHANGE_AVG_CPA_PRICE;
    editorId: string;
    avgCpaPrice: string;
}

export function changeStrategyAvgCpaPrice(editorId: string, avgCpaPrice: string): ChangeStrategyAvgCpaPriceAction {
    return {
        type: StrategyEditorActionType.CHANGE_AVG_CPA_PRICE,
        editorId,
        avgCpaPrice
    };
}

export interface BlurStrategyAvgCpaPriceAction {
    type: StrategyEditorActionType.BLUR_AVG_CPA_PRICE;
    editorId: string;
    constants?: ConstStrategyData;
}

export function blurStrategyAvgCpaPrice(
    editorId: string,
    constants?: ConstStrategyData
): BlurStrategyAvgCpaPriceAction {
    return {
        type: StrategyEditorActionType.BLUR_AVG_CPA_PRICE,
        editorId,
        constants
    };
}

export interface ChangeStrategyContextLimitAction {
    type: StrategyEditorActionType.CHANGE_CONTEXT_LIMIT;
    editorId: string;
    contextLimit: number;
}

export function changeStrategyContextLimit(editorId: string, contextLimit: number): ChangeStrategyContextLimitAction {
    return {
        type: StrategyEditorActionType.CHANGE_CONTEXT_LIMIT,
        editorId,
        contextLimit
    };
}

export interface ChangeStrategyDailyBudgetModeAction {
    type: StrategyEditorActionType.CHANGE_DAILY_BUDGET_MODE;
    editorId: string;
    dailyBudgetMode: GdCampaignBudgetShowMode;
}

export function changeStrategyDailyBudgetMode(
    editorId: string,
    dailyBudgetMode: GdCampaignBudgetShowMode
): ChangeStrategyDailyBudgetModeAction {
    return {
        type: StrategyEditorActionType.CHANGE_DAILY_BUDGET_MODE,
        editorId,
        dailyBudgetMode
    };
}

export interface ChangeStrategyDailyBudgetAction {
    type: StrategyEditorActionType.CHANGE_DAILY_BUDGET;
    editorId: string;
    dailyBudget: string;
}

export function changeStrategyDailyBudget(editorId: string, dailyBudget: string): ChangeStrategyDailyBudgetAction {
    return {
        type: StrategyEditorActionType.CHANGE_DAILY_BUDGET,
        editorId,
        dailyBudget
    };
}

export interface BlurStrategyDailyBudgetAction {
    type: StrategyEditorActionType.BLUR_DAILY_BUDGET;
    editorId: string;
    constants?: ConstStrategyData;
}

export function blurStrategyDailyBudget(
    editorId: string,
    constants?: ConstStrategyData
): BlurStrategyDailyBudgetAction {
    return {
        type: StrategyEditorActionType.BLUR_DAILY_BUDGET,
        editorId,
        constants
    };
}

export interface ChangeStrategyGoalIdAction {
    type: StrategyEditorActionType.CHANGE_GOAL_ID;
    editorId: string;
    goalId: string;
}

export function changeStrategyGoalId(editorId: string, goalId: string): ChangeStrategyGoalIdAction {
    return {
        type: StrategyEditorActionType.CHANGE_GOAL_ID,
        editorId,
        goalId
    };
}

export interface ChangeStrategyMaxClickPriceAction {
    type: StrategyEditorActionType.CHANGE_MAX_CLICK_PRICE;
    editorId: string;
    maxClickPrice: string;
}

export function changeStrategyMaxClickPrice(
    editorId: string,
    maxClickPrice: string
): ChangeStrategyMaxClickPriceAction {
    return {
        type: StrategyEditorActionType.CHANGE_MAX_CLICK_PRICE,
        editorId,
        maxClickPrice
    };
}

export interface BlurStrategyMaxClickPriceAction {
    type: StrategyEditorActionType.BLUR_MAX_CLICK_PRICE;
    editorId: string;
    constants?: ConstStrategyData;
}

export function blurStrategyMaxClickPrice(
    editorId: string,
    constants?: ConstStrategyData
): BlurStrategyMaxClickPriceAction {
    return {
        type: StrategyEditorActionType.BLUR_MAX_CLICK_PRICE,
        editorId,
        constants
    };
}

export interface ChangeStrategyProfitabilityAction {
    type: StrategyEditorActionType.CHANGE_PROFITABILITY;
    editorId: string;
    profitability: string;
}

export function changeStrategyProfitability(
    editorId: string,
    profitability: string
): ChangeStrategyProfitabilityAction {
    return {
        type: StrategyEditorActionType.CHANGE_PROFITABILITY,
        editorId,
        profitability
    };
}

export interface BlurStrategyProfitabilityAction {
    type: StrategyEditorActionType.BLUR_PROFITABILITY;
    editorId: string;
    constants?: ConstStrategyData;
}

export function blurStrategyProfitability(
    editorId: string,
    constants?: ConstStrategyData
): BlurStrategyProfitabilityAction {
    return {
        type: StrategyEditorActionType.BLUR_PROFITABILITY,
        editorId,
        constants
    };
}

export interface ChangeStrategyReserveReturnAction {
    type: StrategyEditorActionType.CHANGE_RESERVE_RETURN;
    editorId: string;
    reserveReturn: number;
}

export function changeStrategyReserveReturn(
    editorId: string,
    reserveReturn: number
): ChangeStrategyReserveReturnAction {
    return {
        type: StrategyEditorActionType.CHANGE_RESERVE_RETURN,
        editorId,
        reserveReturn
    };
}

export interface ChangeStrategyRoiCoefAction {
    type: StrategyEditorActionType.CHANGE_ROI_COEF;
    editorId: string;
    roiCoef: string;
}

export function changeStrategyRoiCoef(editorId: string, roiCoef: string): ChangeStrategyRoiCoefAction {
    return {
        type: StrategyEditorActionType.CHANGE_ROI_COEF,
        editorId,
        roiCoef
    };
}

export interface BlurStrategyRoiCoefAction {
    type: StrategyEditorActionType.BLUR_ROI_COEF;
    editorId: string;
    constants?: ConstStrategyData;
}

export function blurStrategyRoiCoef(editorId: string, constants?: ConstStrategyData): BlurStrategyRoiCoefAction {
    return {
        type: StrategyEditorActionType.BLUR_ROI_COEF,
        editorId,
        constants
    };
}

export interface ChangeStrategySeparateBiddingAction {
    type: StrategyEditorActionType.CHANGE_SEPARATE_BIDDING;
    editorId: string;
}

export function changeStrategySeparateBidding(editorId: string): ChangeStrategySeparateBiddingAction {
    return {
        type: StrategyEditorActionType.CHANGE_SEPARATE_BIDDING,
        editorId
    };
}

export interface ChangeStrategyWeeklyBudgetAction {
    type: StrategyEditorActionType.CHANGE_WEEKLY_BUDGET;
    editorId: string;
    weeklyBudget: string;
}

export function changeStrategyWeeklyBudget(editorId: string, weeklyBudget: string): ChangeStrategyWeeklyBudgetAction {
    return {
        type: StrategyEditorActionType.CHANGE_WEEKLY_BUDGET,
        editorId,
        weeklyBudget
    };
}

export interface BlurStrategyWeeklyBudgetAction {
    type: StrategyEditorActionType.BLUR_WEEKLY_BUDGET;
    editorId: string;
    constants?: ConstStrategyData;
}

export function blurStrategyWeeklyBudget(
    editorId: string,
    constants?: ConstStrategyData
): BlurStrategyWeeklyBudgetAction {
    return {
        type: StrategyEditorActionType.BLUR_WEEKLY_BUDGET,
        editorId,
        constants
    };
}

export interface ValidateAndAttemptSaveAction {
    type: StrategyEditorActionType.VALIDATE_AND_ATTEMPT_SAVE;
    campaignId: CampaignId;
    strategy: StrategyForEditor;
}

export function validateAndAttemptSave(
    campaignId: CampaignId,
    strategy: StrategyForEditor
): ValidateAndAttemptSaveAction {
    return {
        type: StrategyEditorActionType.VALIDATE_AND_ATTEMPT_SAVE,
        campaignId,
        strategy
    };
}

export interface ChangeCpcHoldAction {
    type: StrategyEditorActionType.CHANGE_CPC_HOLD;
    editorId: string;
}

export function changeCpcHold(editorId: string): ChangeCpcHoldAction {
    return {
        type: StrategyEditorActionType.CHANGE_CPC_HOLD,
        editorId
    };
}

export interface ApplyStrategyValidationResultAction {
    type: StrategyEditorActionType.APPLY_VALIDATION_RESULT;
    editorId: string;
    validationResult: StrategyEditorValidationResult;
}

export function applyStrategyEditorValidationResult(
    editorId: string,
    validationResult: StrategyEditorValidationResult
): ApplyStrategyValidationResultAction {
    return {
        type: StrategyEditorActionType.APPLY_VALIDATION_RESULT,
        editorId,
        validationResult
    };
}

export interface RevertStrategyEditorAction {
    type: StrategyEditorActionType.REVERT_EDITING;
    editorId: string;
    strategy: Strategy;
}

export function revertStrategyEditor(editorId: string, strategy: Strategy): RevertStrategyEditorAction {
    return {
        type: StrategyEditorActionType.REVERT_EDITING,
        editorId,
        strategy
    };
}

export interface ChangeOptimizationCriteriaAction {
    type: StrategyEditorActionType.CHANGE_OPTIMIZATION_CRITERIA;
    editorId: string;
    optimizationCriteria: OptimizationCriteria;
}

export function changeOptimizationCriteria(
    editorId: string,
    optimizationCriteria: OptimizationCriteria
): ChangeOptimizationCriteriaAction {
    return {
        type: StrategyEditorActionType.CHANGE_OPTIMIZATION_CRITERIA,
        editorId,
        optimizationCriteria
    };
}

export interface ChangeStrategyWeekBundleAction {
    type: StrategyEditorActionType.CHANGE_WEEK_BUNDLE;
    editorId: string;
    weekBundle: string;
}

export function changeStrategyWeekBundle(editorId: string, weekBundle: string): ChangeStrategyWeekBundleAction {
    return {
        type: StrategyEditorActionType.CHANGE_WEEK_BUNDLE,
        editorId,
        weekBundle
    };
}

export interface BlurStrategyWeekBundleAction {
    type: StrategyEditorActionType.BLUR_WEEK_BUNDLE;
    editorId: string;
    constants?: ConstStrategyData;
}

export function blurStrategyWeekBundle(editorId: string, constants?: ConstStrategyData): BlurStrategyWeekBundleAction {
    return {
        type: StrategyEditorActionType.BLUR_WEEK_BUNDLE,
        editorId,
        constants
    };
}

export interface ToggleAuxiliaryParamsIsOpenAction {
    type: StrategyEditorActionType.TOGGLE_AUXILIARY_PARAMS_VISIBILTY;
    editorId: string;
}

export function toggleAuxiliaryParamsIsOpen(editorId: string): ToggleAuxiliaryParamsIsOpenAction {
    return {
        type: StrategyEditorActionType.TOGGLE_AUXILIARY_PARAMS_VISIBILTY,
        editorId
    };
}

export interface HideRoiMeaningfulGoalsTooltipAction {
    type: StrategyEditorActionType.HIDE_ROI_MEANINGFUL_GOALS_TOOLTIP;
}

export function hideRoiMeaningfulGoalsTooltip(): HideRoiMeaningfulGoalsTooltipAction {
    return {
        type: StrategyEditorActionType.HIDE_ROI_MEANINGFUL_GOALS_TOOLTIP
    };
}

export interface ClearRoiGoalIdErrorAction {
    type: StrategyEditorActionType.CLEAR_ROI_GOAL_ID_ERROR;
    editorId: string;
}

export function clearRoiGoalIdError(editorId: string): ClearRoiGoalIdErrorAction {
    return {
        type: StrategyEditorActionType.CLEAR_ROI_GOAL_ID_ERROR,
        editorId
    };
}

export interface ToggleCpaPayForConversionAction {
    type: StrategyEditorActionType.TOGGLE_CPA_PAY_FOR_CONVERSION;
    editorId: string;
}

export function toggleCpaPayForConversion(editorId: string): ToggleCpaPayForConversionAction {
    return {
        type: StrategyEditorActionType.TOGGLE_CPA_PAY_FOR_CONVERSION,
        editorId
    };
}

export type StrategyEditorAction =
    | InitStrategyEditorsAction
    | ToggleCpaPayForConversionAction
    | OpenStrategyEditorAction
    | CloseStrategyEditorAction
    | ChangeStrategyTypeAction
    | ChangeStrategyPlatformAction
    | ChangeStrategyAttributionModelAction
    | ChangeStrategyAvgClickPriceAction
    | ChangeStrategyAvgCpaPriceAction
    | ChangeStrategyContextLimitAction
    | ChangeStrategyDailyBudgetModeAction
    | ChangeStrategyDailyBudgetAction
    | ChangeStrategyGoalIdAction
    | ChangeStrategyMaxClickPriceAction
    | ChangeStrategyProfitabilityAction
    | ChangeStrategyReserveReturnAction
    | ChangeStrategyRoiCoefAction
    | ChangeStrategySeparateBiddingAction
    | ChangeStrategyWeeklyBudgetAction
    | ValidateAndAttemptSaveAction
    | ChangeCpcHoldAction
    | ApplyStrategyValidationResultAction
    | RevertStrategyEditorAction
    | ChangeOptimizationCriteriaAction
    | ChangeStrategyWeekBundleAction
    | BlurStrategyAvgClickPriceAction
    | BlurStrategyWeekBundleAction
    | BlurStrategyWeeklyBudgetAction
    | BlurStrategyAvgCpaPriceAction
    | BlurStrategyRoiCoefAction
    | BlurStrategyDailyBudgetAction
    | BlurStrategyMaxClickPriceAction
    | BlurStrategyProfitabilityAction
    | ToggleAuxiliaryParamsIsOpenAction
    | HideRoiMeaningfulGoalsTooltipAction
    | ClearRoiGoalIdErrorAction;
